import queryString from "query-string";
import AxiosInstance, { takeLatestRequest } from "@/config/AxiosInstance";
import { API_BASE_URL_ADMIN, API_BASE_URL } from "@/config/appConfig";
import type {
  ResourceCenterApiResponse,
  ResourceCenterRequestParams,
} from "@/features/education-center/types";

type QueryParams = Record<string, unknown>;

interface BrandScopedRequest {
  brandId: number | string;
}

export const brandService = {
  getPageSetup,
  getBrands,
  getBrandLocations,
  getBrandBySlug,
  getResourceCenterData,
  saveUserFeedback,
  getBrandProfileDetails,
  requestChannelAccess,
  getBrandParticipants,
  getBrandGroups,
};

function getPageSetup(brand_id: number | string) {
  const getData = {
    status: 1,
    row: 50,
    page: 1,
    is_deleted: 0,
    brand_id: brand_id,
  };
  const query = queryString.stringify(getData);
  return AxiosInstance.get(`${API_BASE_URL_ADMIN}/pageSetup?${query}`);
}

function getBrands(data: QueryParams = {}) {
  const getData = {
    status: 1,
    row: 50,
    page: 1,
    is_deleted: 0,
    id: "",
    fk_participant_id: 0,
    fk_company_id: 0,
    ...data,
  };
  const query = queryString.stringify(getData);
  return AxiosInstance.get(`${API_BASE_URL_ADMIN}/brand?${query}`);
}

function getBrandLocations(data: QueryParams = {}, isSWR = false) {
  const getData = {
    id: "",
    row: 10,
    page: 1,
    searchKey: "",
    groupId: "",
    ...data,
  };
  const config = {
    method: "get",
    url: `${API_BASE_URL_ADMIN}/locationReportListing`,
    params: getData,
  };
  if (isSWR) {
    return takeLatestRequest(config, "locationReportListing").then(
      (r: { data: unknown }) => r.data
    );
  }
  return takeLatestRequest(config, "locationReportListing");
}

function getBrandBySlug(slug: string) {
  return AxiosInstance.get(`${API_BASE_URL}/brand/${slug}`);
}

function getResourceCenterData(
  _data: ResourceCenterRequestParams,
  _isSWR: true
): Promise<ResourceCenterApiResponse>;
function getResourceCenterData(
  _data: ResourceCenterRequestParams,
  _isSWR?: false
): ReturnType<typeof AxiosInstance.get>;
function getResourceCenterData(
  data: ResourceCenterRequestParams,
  isSWR = false
) {
  const query = queryString.stringify(data);
  if (isSWR) {
    return AxiosInstance.get(
      `${API_BASE_URL_ADMIN}/resource-center?${query}`
    ).then((r) => r.data);
  }
  return AxiosInstance.get(`${API_BASE_URL_ADMIN}/resource-center?${query}`);
}

function saveUserFeedback(data: QueryParams) {
  return AxiosInstance.post(`${API_BASE_URL_ADMIN}/participantFeedback`, data);
}

function getBrandProfileDetails(data: QueryParams) {
  return AxiosInstance.post(`${API_BASE_URL_ADMIN}/brandProfileDetails`, data);
}

function requestChannelAccess(data: QueryParams) {
  return AxiosInstance.post(
    `${API_BASE_URL_ADMIN}/brandProfileChannelActive`,
    data
  );
}

function getBrandParticipants(data: BrandScopedRequest, isSWR = false) {
  if (isSWR) {
    return AxiosInstance.get(
      `${API_BASE_URL_ADMIN}/brand/${data.brandId}/participants`
    ).then((r) => r.data);
  }
  return AxiosInstance.get(
    `${API_BASE_URL_ADMIN}/brand/${data.brandId}/participants`
  );
}

function getBrandGroups(data: BrandScopedRequest, isSWR = false) {
  if (isSWR) {
    return AxiosInstance.get(
      `${API_BASE_URL_ADMIN}/brand/${data.brandId}/groups`
    ).then((r) => r.data);
  }
  return AxiosInstance.get(
    `${API_BASE_URL_ADMIN}/brand/${data.brandId}/groups`
  );
}
